import { Check } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { Asset } from '@/api/common-types'
import { useListAssets } from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Button } from '@/components/base/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/base/dialog'
import { Text } from '@/components/base/text'
import { useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'

export function AssetLibraryPicker({
	open,
	onOpenChange,
	attachedAssetUuids,
	onSelect,
	title,
	emptyLabel,
	allAttachedLabel
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	attachedAssetUuids: Set<string>
	onSelect: (asset: Asset) => Promise<void>
	title: string
	emptyLabel: string
	allAttachedLabel: string
}) {
	const { L } = useI18n()
	const [selecting, setSelecting] = useState<string | null>(null)

	const assetsQuery = useListAssets({ category: ['PRODUCT'], limit: 100, offset: 0 }, { enabled: open })

	const assets = useMemo(() => assetsQuery.data?.assets ?? [], [assetsQuery.data])
	const selectableCount = useMemo(
		() => assets.filter(asset => !attachedAssetUuids.has(asset.uuid)).length,
		[assets, attachedAssetUuids]
	)

	const handleSelect = async (asset: Asset) => {
		if (attachedAssetUuids.has(asset.uuid) || selecting) return
		setSelecting(asset.uuid)
		try {
			await onSelect(asset)
		} finally {
			setSelecting(null)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='max-h-[min(85vh,720px)] overflow-hidden border-ui-border-base bg-ui-bg-base sm:max-w-3xl'>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<div className='max-h-[60vh] overflow-y-auto'>
					{assetsQuery.isLoading ? (
						<Text className='text-sm text-ui-fg-subtle'>{L.General.Loading()}</Text>
					) : assetsQuery.isError ? (
						<div className='flex flex-col items-start gap-2'>
							<Text className='text-sm text-ui-fg-subtle'>
								{L.ApiErrorAlert.AnErrorOccurredWhileFetchingData()}
							</Text>
							<Button
								type='button'
								variant='secondary'
								size='small'
								onClick={() => assetsQuery.refetch()}
							>
								{L.General.TryAgain()}
							</Button>
						</div>
					) : assets.length === 0 ? (
						<Text className='text-sm text-ui-fg-subtle'>{emptyLabel}</Text>
					) : selectableCount === 0 ? (
						<Text className='text-sm text-ui-fg-subtle'>{allAttachedLabel}</Text>
					) : (
						<div className='grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'>
							{assets.map(asset => {
								const isAttached = attachedAssetUuids.has(asset.uuid)
								return (
									<button
										key={asset.uuid}
										type='button'
										disabled={isAttached || selecting !== null}
										onClick={() => handleSelect(asset)}
										className={clx(
											'group relative flex flex-col overflow-hidden rounded-lg border border-ui-border-base bg-ui-bg-base text-left outline-none transition-fg',
											isAttached
												? 'cursor-not-allowed opacity-60'
												: 'cursor-pointer hover:shadow-elevation-card-hover focus-visible:ring-2 focus-visible:ring-ui-border-interactive'
										)}
									>
										<AssetImage asset={asset} className='aspect-square w-full' />
										{isAttached && (
											<div className='absolute right-1.5 top-1.5 flex items-center gap-1 rounded-md bg-ui-bg-base px-1.5 py-0.5 shadow-borders-base'>
												<Check className='size-3.5 text-ui-fg-interactive' />
											</div>
										)}
										<Text
											size='xsmall'
											className='truncate border-t border-ui-border-base px-2 py-1 text-ui-fg-subtle'
											title={asset.filename}
										>
											{asset.filename}
										</Text>
									</button>
								)
							})}
						</div>
					)}
				</div>
				<DialogFooter>
					<Button type='button' variant='secondary' onClick={() => onOpenChange(false)}>
						{L.General.Close()}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
